import { useState, useEffect } from 'react'
import { useMutation } from "@tanstack/react-query"

import { showLoading } from 'services/util'
import copyIcon from '../components/images/copy-icon.svg'

import { graphql } from '../api'
import { isAuctionOwner } from './permissionsutil'
import { updateAuctionMutation } from '../graphql/Mutations'

import css from './sass/createedit.module.scss'
import themeCss from '../components/sass/theme.module.scss'
import { getAuctionStatus, forceTimezone, displayUtcInPacific } from '../util/dateString'
import { getImageUrl } from '../util/images'
import { history } from 'services/history'
import { normalizeRichTextForStorage } from './auctionsadminutil'
import { setSessionItem } from '../components/utils'

import UploadFiles from './UploadFiles'
import AuctionEditPermissions from './AuctionEditPermissions'
import InputMarketingEffort from './InputMarketingEffort'
import InputFundNumber from './InputFundNumber'
import TextEditor from './TextEditor'
import InputFundSearch from './InputFundSearch'

//taking in the stored unsaved auction data to populate the form with saved changes
const getInitialAuctionChangesState = (auctionCode) => {
  //detect if this was an actual page reload, or back/forward navigation
  const navEntries = performance.getEntriesByType('navigation')
  const navigationType = navEntries.length > 0 ? navEntries[0].type : 'unknown'

  //if page was reloaded (user pressed refres), clear unsaved changes
  // if the user navigates away from the page (must click Leave on Chrome pop up) and hits back/forward
  if (navigationType === 'reload' || navigationType === 'back_forward') {
    return { auctionChanges: null, shouldClearUnsavedAuction: true }
  }

  // otherwise, load from session storage if it matches current auction
  const stored = sessionStorage.getItem('unsavedAuction')
  const parsed = stored ? JSON.parse(stored) : null

  if (parsed && parsed.auctionCode === auctionCode) {
    return { auctionChanges: parsed, shouldClearUnsavedAuction: false }
  }

  return { auctionChanges: null, shouldClearUnsavedAuction: Boolean(parsed) }
}

export default function AuctionEdit({ auction, refreshAdminAuctionQuery, unsavedAuction, setUnsavedAuction }) {
  // to handle the warning on loading with undefined values for inputs
  const inputValue = (value) => value ?? ''

  // console.log('auction', auction)
  const [auctionCode, setAuctionCode] = useState(auction?.AuctionCode)
  const [auctionLocation, setAuctionLocation] = useState(auction?.AuctionLocation)
  const [bannerImage, setBannerImage] = useState(auction?.BannerImageName)
  const [description, setDescription] = useState(auction?.Description)
  const [endTime, setEndTime] = useState(auction?.EndTime?.substring(0, 16))
  const [needsCheckin, setNeedsCheckin] = useState(auction?.NeedsCheckin || false)
  const [allowVisitorView, setAllowVisitorView] = useState(auction?.AllowVisitorView === false ? false : true)
  const [startTime, setStartTime] = useState(auction?.StartTime?.substring(0, 16))
  const [title, setTitle] = useState(auction?.Title)
  const [eventRegSiteUrl, setEventRegSiteUrl] = useState(auction?.EventRegSiteUrl)
  const [permissions, setPermissions] = useState(auction?.Permissions)

  const [supportEmail, setSupportEmail] = useState(auction?.SupportEmail)
  const [fundName, setFundName] = useState(auction?.FundName)
  const [fundNumber, setFundNumber] = useState(auction?.FundNumber)

  const [marketingEffortNumber, setMarketingEffortNumber] = useState(auction?.MarketingEffortNumber)
  const [marketingEffortName, setMarketingEffortName] = useState(auction?.MarketingEffortName)

  // ADDED: Store raw input values for session storage (regardless of validity)
  const [fundNumberInput, setFundNumberInput] = useState()
  const [marketingEffortInput, setMarketingEffortInput] = useState()

  //created variables to store the pacific time for display purposes
  // editing needed a stored value for display and one to submit to the database
  const [pacificStartTime, setPacificStartTime] = useState()
  const [pacificEndTime, setPacificEndTime] = useState()

  const [needsRequired, setNeedsRequired] = useState(false)
  const [invalidStart, setInvalidStart] = useState('')
  const [invalidEnd, setInvalidEnd] = useState('')
  const [needsTrim, setNeedsTrim] = useState(false)
  
  const [disableEdit, setDisableEdit] = useState(true)
  const [bannerUrl, setBannerUrl] = useState('')

  // const [hasDonation, setHasDonation] = useState(auction?.HasDonation === true ? true : false)
  // const [includeFundraisingGoal, setIncludeFundraisingGoal] = useState(auction?.IncludeFundraisingGoal === true ? true : false)
  // const [fundraisingGoal, setFundraisingGoal] = useState(auction?.FundraisingGoal)
  // const [lowDonationAmount, setLowDonationAmount] = useState(auction?.LowDonationAmount)
  // const [midDonationAmount, setMidDonationAmount] = useState(auction?.MidDonationAmount)
  // const [highDonationAmount, setHighDonationAmount] = useState(auction?.HighDonationAmount)

  const [linkToCopy, setLinkToCopy] = useState('')
  const [withEmail, setWithEmail] = useState(false)
  const [withPublicId, setWithPublicId] = useState(false)

  const [invalidPermissions, setInvalidPermissions] = useState(false)

  const [copied, setCopied] = useState(false)

  const isUserSR = localStorage.getItem('isSolicitationReviewAdmin')

  const isSrApproved = auction?.ReviewStatus === "Approved"

  //taking in the stored unsaved auction data to populate the form with saved changes
  const [initialAuctionChangesState] = useState(() => getInitialAuctionChangesState(auction?.AuctionCode))
  const { auctionChanges, shouldClearUnsavedAuction } = initialAuctionChangesState

  useEffect(() => {
    if (shouldClearUnsavedAuction) {
      setSessionItem('unsavedAuction', null)
    }
  }, [shouldClearUnsavedAuction])

  //render only on load or when auction changes
  useEffect(() => {

    if (auctionChanges !== null && auctionChanges.auctionCode === auction?.AuctionCode) {
      setAuctionCode(auctionChanges?.auctionCode)
      setAuctionLocation(auctionChanges?.auctionLocation)
      setDescription(auctionChanges?.description)
      setNeedsCheckin(auctionChanges?.needsCheckin)
      setAllowVisitorView(auctionChanges?.allowVisitorView)
      setTitle(auctionChanges?.title)
      setSupportEmail(auctionChanges?.supportEmail)
      // due to the time zone display difficulties, need to set the pacific time, which then sets the start and end time
      setPacificEndTime(auctionChanges?.pacificEndTime?.substring(0, 16))
      setPacificStartTime(auctionChanges?.pacificStartTime?.substring(0, 16))
      setEventRegSiteUrl(auctionChanges?.eventRegSiteUrl)

      // IMPORTANT: Don't set fund and marketing effort here - let child components handle it
      // This prevents race conditions where parent and child both try to set the same values
      // setFundName(auctionChanges?.fundName)
      // setFundNumber(auctionChanges?.fundNumber)
      // setMarketingEffortName(auctionChanges?.marketingEffortName)
      // setMarketingEffortNumber(auctionChanges?.marketingEffortNumber)
      // setHasDonation(auctionChanges?.hasDonation)
      // setIncludeFundraisingGoal(auctionChanges?.includeFundraisingGoal)
      // setFundraisingGoal(auctionChanges?.fundraisingGoal)
      // setLowDonationAmount(auctionChanges?.lowDonationAmount)
      // setMidDonationAmount(auctionChanges?.midDonationAmount)
      // setHighDonationAmount(auctionChanges?.highDonationAmount)
    } 
  }, [auction?.AuctionId])

  useEffect(() => {

    if (unsavedAuction) {
      const unsavedObj = {
        auctionCode, auctionLocation, description, pacificEndTime, needsCheckin, pacificStartTime, title, allowVisitorView, eventRegSiteUrl,
        // hasDonation, includeFundraisingGoal, fundraisingGoal, lowDonationAmount, midDonationAmount, highDonationAmount,
        fundNumberInput, fundNumber, fundName, permissions, supportEmail,
        marketingEffortName, marketingEffortNumber, marketingEffortInput
      }
      setSessionItem('historyProps', null)
      setSessionItem('unsavedAuction', unsavedObj)
    } else {
      setSessionItem('unsavedAuction', null)
      setSessionItem('historyProps', null)
    }
  }, [unsavedAuction, auctionCode, auctionLocation, description, pacificEndTime, needsCheckin, pacificStartTime, title, eventRegSiteUrl,
    // hasDonation, includeFundraisingGoal, fundraisingGoal, lowDonationAmount, midDonationAmount, highDonationAmount,
    allowVisitorView, fundNumber,
     marketingEffortInput, fundNumberInput, marketingEffortName, marketingEffortNumber,
     fundName, permissions, supportEmail])


  // if the start of end time is not defined in the edit panel, the auction values should be null to match a missing value
  // once the time is set, they won't be able to remove it from the admin panel
  useEffect(() => {
    
    // Only set times from auction DB if auctionChanges doesn't have them (initial load from DB)
    if (!auctionChanges) {
      if (auction?.StartTime) setPacificStartTime(displayUtcInPacific(auction.StartTime))
      else setPacificStartTime(null)

      if (auction?.EndTime) setPacificEndTime(displayUtcInPacific(auction.EndTime))
      else setPacificEndTime(null)
    } 
  }, [auction.EndTime, auction.StartTime])

  const liveStatus = getAuctionStatus(auction?.StartTime, auction?.EndTime)

  let isReadOnly = ((auction.Status === 'CLOSED' || liveStatus !== 'before') && auction.Status !== 'PREVIEW') || !isAuctionOwner(auction) || isUserSR

  const italicLabel = '<i>text to italicize</i>'

  const handleCopy = () => {
    navigator.clipboard.writeText(linkToCopy)
    setCopied(true)
    // Hide tooltip after 2 seconds
    setTimeout(() => {
      setCopied(false)
    }, 1000)
  }

  const { data: updateAuctionResponse, error, isPending, mutate: saveAuction } = useMutation({
    mutationFn: (input) => {
      return graphql({ query: updateAuctionMutation, variables: { input: input } })
    },
  })

  useEffect(() => {
    if (pacificEndTime) setEndTime(forceTimezone(pacificEndTime, "America/Los_Angeles"))
    if (pacificStartTime) setStartTime(forceTimezone(pacificStartTime, "America/Los_Angeles"))
  }, [pacificEndTime, pacificStartTime])

  //for when auction admin extends the auction duration while live, need to update the end time in edit auction
  useEffect(() => {
    if (auction.Status === 'PUBLIC') setPacificEndTime(displayUtcInPacific(auction.EndTime, "America/Los_Angeles"))
  }, [auction.EndTime, auction.Status])

  useEffect(() => {

    if (!auction) return

    const checkForNewValue = (val1, val2) => {
      //need condition if the value is null from auction details, it will read as undefined after one save once the page reloads
      if (val2 === null && val1 === undefined) return false

      // TO DO: should we either say null and '' are the same thing?
      // or should we submit any edit with blank strings to help prevent issues later
      // to help prevent null vs '' being seen as different values on comparisons when coming back to auction
      if (val2 === null && val1 === '') return false
      return val1 !== val2
    }

    // Special comparison function for fund and marketing effort inputs
    // to handle null database values from newly created auctions that have not been saved yet
    const checkForNewSearchedValue = (val1, val2) => {
      // If database value is null and input value is empty string, treat as no change
      if (val2 === null && (val1 === '' || val1 === undefined)) return false
      if (val1 === undefined) return false
      return val1 !== val2
    }

    const checkForNewTimeValue = (val1, val2) => {
      //need condition if the value is null from auction details, it will read as undefined after one save once the page reloads
      // if (val2 === null && val1 === undefined) return false
      // if (val2 === null && val1 === null) return false

      //check the date times values in YYYY-MM-DD T HH:MM format
      return val1?.slice(0, 16) !== val2?.slice(0, 16)
    }

    const hasMarketingChanged = () => {
      const isEmpty = v => v == null || v === "" // covers null and undefined
  
      const numEmpty = isEmpty(marketingEffortNumber)
      const inputEmpty = isEmpty(marketingEffortInput)
    
      // Case 1: both empty -> false
      if (numEmpty && inputEmpty) return false
      // Case 2: number empty, input non-empty -> true
      if (numEmpty && !inputEmpty) return true
      // Case 3: number non-empty, input empty -> true
      if (!numEmpty && inputEmpty) return true
      // Case 4 & 5: both non-empty -> true if they don't match, false if they match
      const normalizedNumber = marketingEffortNumber.trim()
      const normalizedInput  = marketingEffortInput.trim()
      return normalizedNumber !== `${normalizedInput}`
    }

    // const checkForNewDonationValues = () => {
    //   const hasDonationChanged = checkForNewValue(hasDonation, auction.HasDonation)

    //   // If the current form says there is no donation, only hasDonation matters.
    //   if (!hasDonation) {
    //     return hasDonationChanged
    //   }

    //   // inputs return strings, DB returns numbers — coerce both to number for comparison
    //   const toNum = (v) => (v === '' || v === null || v === undefined) ? null : Number(v)

    //   const lowChanged = toNum(lowDonationAmount) !== toNum(auction.LowDonationAmount)
    //   const midChanged = toNum(midDonationAmount) !== toNum(auction.MidDonationAmount)
    //   const highChanged = toNum(highDonationAmount) !== toNum(auction.HighDonationAmount)
    //   const includeGoalChanged = checkForNewValue(includeFundraisingGoal, auction.IncludeFundraisingGoal)

    //   // fundraisingGoal only matters when includeFundraisingGoal is true
    //   const goalChanged = includeFundraisingGoal
    //     ? toNum(fundraisingGoal) !== toNum(auction.FundraisingGoal)
    //     : false

    //   return (
    //     hasDonationChanged ||
    //     lowChanged ||
    //     midChanged ||
    //     highChanged ||
    //     includeGoalChanged ||
    //     goalChanged
    //   )
    // }

    const checkForNewPermissions = (val1, val2) => {

      if (val1.length !== val2.length) return true

      // stringify objects consistently
      const normalize = obj => JSON.stringify({
        Email: obj.Email || '',
        Level: obj.Level || ''
      })

      const countOccurrences = (arr) => {
        const map = new Map()
        for (const obj of arr) {
          const key = normalize(obj)
          map.set(key, (map.get(key) || 0) + 1)
        }
        return map
      }

      const map1 = countOccurrences(val1)
      const map2 = countOccurrences(val2)

      if (map1.size !== map2.size) return true

      for (const [key, count] of map1) {
        if (map2.get(key) !== count) return true
      }

      return false

    }

    //check to see if anything has changed, any of the check functions returning true means there is a change
    // TO DO - once form is stable, break these functions into auctionsadminutil
    if (
      checkForNewTimeValue(endTime, auction.EndTime) ||
      checkForNewTimeValue(startTime, auction.StartTime) ||
      checkForNewValue(auctionCode, auction.AuctionCode) ||
      checkForNewValue(auctionLocation, auction.AuctionLocation) ||
      checkForNewValue(description, auction.Description) ||
      checkForNewValue(needsCheckin, auction.NeedsCheckin) ||
      checkForNewValue(title, auction.Title) ||
      checkForNewValue(allowVisitorView, auction.AllowVisitorView) ||
      checkForNewValue(supportEmail, auction.SupportEmail) ||
      checkForNewValue(eventRegSiteUrl, auction.EventRegSiteUrl) ||
      checkForNewPermissions(permissions, auction.Permissions) ||
      checkForNewSearchedValue(fundName, auction.FundName) ||
      checkForNewSearchedValue(fundNumber, auction.FundNumber) ||
      checkForNewSearchedValue(marketingEffortName, auction.MarketingEffortName) || 
      checkForNewSearchedValue(marketingEffortNumber, auction.MarketingEffortNumber)
      // checkForNewDonationValues()
      // checkForNewValue(fundNumberInput, auction.FundNumber) 
      // hasMarketingChanged() 
    ) {
      setDisableEdit(false)
      // if we want to save the input values for the fund and marketing effort search, we would have to do it here
      // this would allow it to save in session without affecting the disable edit / save button at the bottom
      //use hasMarketingChanged() func and make one for fund search? or maybe just pass those down into the individual components?
      setUnsavedAuction(true)
    }
    else {
      setDisableEdit(true)
      setUnsavedAuction(false)
    }

    //check to see if there is an invalid start or end time
    if (invalidStart !== '' || invalidEnd !== '') setDisableEdit(true)

    // validate the donation elements 
    // if (hasDonation && (
    //     (lowDonationAmount === '' || midDonationAmount === '' || highDonationAmount === '') ||
    //     (lowDonationAmount === null || midDonationAmount === null || highDonationAmount === null) ||
    //     (includeFundraisingGoal && (fundraisingGoal === '' || fundraisingGoal === null))
    //   )) setDisableEdit(true)


  }, [auction, auctionCode, auctionLocation, bannerImage, description, endTime, needsCheckin, allowVisitorView, startTime, title,
    // hasDonation, includeFundraisingGoal, fundraisingGoal, lowDonationAmount, midDonationAmount, highDonationAmount,
    eventRegSiteUrl, supportEmail, fundName, fundNumber, marketingEffortName, marketingEffortNumber, 
    invalidEnd, invalidStart,
    permissions, setUnsavedAuction, fundNumberInput, marketingEffortInput])

  useEffect(() => {
    if (auctionCode === "" || title === "") setNeedsRequired(true)
    else setNeedsRequired(false)
  }, [auctionCode, title])

  //needed to refresh the auction details from inside upload files component properly
  useEffect(() => {
    setBannerImage(auction.BannerImageName) // fine maybe?
  }, [auction])

  useEffect(() => {
    showLoading(isPending)

    if (error) {
      console.log("Auction not created: ", error)
      console.log(error.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
    }
    else if (updateAuctionResponse) {
      console.log(updateAuctionResponse)
      console.log('Auction updated: ', updateAuctionResponse.data.updateAuction.AuctionId)

      //after a save, set unsaved auction to null
      setSessionItem('unsavedAuction', null)

      refreshAdminAuctionQuery()
      
      //trim after submission to update input fields
      if (needsTrim) {
        setTitle(title?.trim())
        setStartTime(startTime?.trim())
        setSupportEmail(supportEmail?.trim())
        setFundName(fundName?.trim())
        setFundNumber(fundNumber?.trim())
        setMarketingEffortName(marketingEffortName?.trim())
        setMarketingEffortNumber(marketingEffortNumber?.trim())
        setNeedsCheckin(needsCheckin)
        setAllowVisitorView(allowVisitorView)
        setEndTime(endTime?.trim())
        setBannerImage(bannerImage?.trim())
        setDescription(normalizeRichTextForStorage(description))
        setAuctionLocation(auctionLocation?.trim())
        setEventRegSiteUrl(eventRegSiteUrl?.trim())
        // setHasDonation(hasDonation)
        // setIncludeFundraisingGoal(includeFundraisingGoal)
        // setFundraisingGoal(fundraisingGoal)
        // setLowDonationAmount(lowDonationAmount)
        // setMidDonationAmount(midDonationAmount)
        // setHighDonationAmount(highDonationAmount)

        setNeedsTrim(false)
      }

    }
  }, [updateAuctionResponse, error, isPending, refreshAdminAuctionQuery, title, startTime, supportEmail, fundName, fundNumber, eventRegSiteUrl,
    endTime, bannerImage, description, auctionLocation, needsCheckin, allowVisitorView, permissions, marketingEffortName, marketingEffortNumber])

  // use effect for auction banner image
  useEffect(() => {
    const url = getImageUrl(auction?.AuctionCode, 'banner', bannerImage)
    setBannerUrl(url)
  }, [auction, bannerImage])

  // for need check in, currently hidden from user
  // const handleNeedCheckin = (e) => {
  //   const newValue = e.target.value
  //   if (newValue === 'true') setNeedsCheckin(true)
  //   if (newValue === 'false') setNeedsCheckin(false)
  // }

  const handleAllowVisitorView = (e) => {
    const newValue = e.target.value
    if (newValue === 'true') setAllowVisitorView(true)
    if (newValue === 'false') setAllowVisitorView(false)
  }

  useEffect(() => {
    //only need to check if both startTime and endTime are not null
    if (!startTime || !endTime) return

    const now = new Date()
    const startDate = new Date(startTime)
    const endDate = new Date(endTime)

    setInvalidStart('')
    setInvalidEnd('')

    //can set up to only allow events to be set up a week, a day in advance, etc.
    if (now > startDate) setInvalidStart('Start date needs to be after current date and time.')
    if (endDate < startDate) setInvalidEnd('End date needs to be after the start date time.')
    if (now > endDate) setInvalidEnd('End date needs to be after current date and time.')

    //if the auction has already started or ended, then no error message is needed
    if (liveStatus === 'ended' || liveStatus === 'started') {
      setInvalidStart('')
      setInvalidEnd('')
    }

    //takes into account if either start or end time is not complete
    if (startTime?.length === 0) setInvalidStart('Please enter a start time.')
    if (endTime?.length === 0) setInvalidEnd('Please enter an end time.')

  }, [startTime, endTime, liveStatus])

  useEffect(() => {

    let linkStr = `${window.location.origin}/flow/auction?auctionId=${auctionCode}`
    if (withPublicId) linkStr += '&publicId=public_id_goes_here'
    if (withEmail) linkStr += '&email=joebruin@example.com'
    setLinkToCopy(linkStr)
  }, [withEmail, withPublicId, auctionCode])

  //check if the permissions are valid any time they change
  useEffect(() => {

    if (permissions.length === 0) setInvalidPermissions(false)

    for (let i = 0; i < permissions.length; i++) {
      if (permissions[i].Email === '') {
        setInvalidPermissions(true)
        return
      }
      if (!(permissions[i].Level === 'View' || permissions[i].Level === 'Edit')) {
        setInvalidPermissions(true)
        return
      }
      //if the last Email and Level are valid, then set invalidPermissions to false to allow submit
      if (i === permissions.length - 1) setInvalidPermissions(false)
    }
  }, [permissions])

  // warn on tab close or refresh
  useEffect(() => {
    const handleBeforeUnload = (event) => {

      if (unsavedAuction) {
        // trigger the browser dialog
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    // Make handler removable from outside components for intentional navigation
    // This allows PreviewPublish to remove the handler before navigation to prevent popups
    const removeBeforeUnloadHandler = () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
    window.removeAuctionEditBeforeUnload = removeBeforeUnloadHandler

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      delete window.removeAuctionEditBeforeUnload
    }
  }, [unsavedAuction])

  //clear out unsaved auction and disable pop up if auction is read only
  // for when auction has started, is closed, or solicitation or collaborator view
  useEffect(() => {

    if (unsavedAuction && isReadOnly) {
      setSessionItem('unsavedAuction', null)
      setUnsavedAuction(false)
    }

  }, [isReadOnly, unsavedAuction])

  const onSubmit = async () => {
    const normalizedDescription = normalizeRichTextForStorage(description)

    const input = {
      Title: title.trim(),
      StartTime: startTime,
      SupportEmail: supportEmail?.trim(),
      FundName: fundName?.trim(),
      FundNumber: fundNumber?.trim(),
      MarketingEffortName: marketingEffortName?.trim(),
      MarketingEffortNumber: marketingEffortNumber?.trim(),
      NeedsCheckin: needsCheckin,
      AllowVisitorView: allowVisitorView,
      EndTime: endTime,
      BannerImageName: bannerImage?.trim(),
      Description: normalizedDescription,
      AuctionLocation: auctionLocation?.trim(),
      AuctionId: auction.AuctionId,
      Status: auction.Status,
      EventRegSiteUrl: eventRegSiteUrl?.trim(),
      // HasDonation: hasDonation,
      // IncludeFundraisingGoal: includeFundraisingGoal,
      // FundraisingGoal: fundraisingGoal,
      // LowDonationAmount: lowDonationAmount,
      // MidDonationAmount: midDonationAmount,
      // HighDonationAmount: highDonationAmount,
    }

    // need this in case user removes the fundraising goal, an empty value causes an error in the save
    // if (includeFundraisingGoal && fundraisingGoal === '') input['FundraisingGoal'] = null

    // don't sent anything if user doesn't have permission to update permissions
    // AdminPermissions = [] removes all admin permissions from this auction
    // AdminPermissions missing makes no changes
    if (permissions) input.AdminPermissions = permissions

    console.log('input', input)

    saveAuction(input)
    setNeedsTrim(true)
  }

  if (!auction) return <></>

  return (
    <div className={css['edit-container']}>
        <span hidden id="createdAuctionId">{auction.AuctionId}</span> {/* used by e2e tests to clean up */}
        <p className={css['label-notice']}>All fields required to submit for Soliciation Review</p>
      <div className={css['item-container']}>
        <div className={css['edit-item-input']}>
          <label>Title</label>
          <input id="editAuctionTitle" type='text'
            placeholder='e.g. UCLA Auction'
            value={inputValue(title)}
            disabled={isReadOnly || isSrApproved}
            onChange={(e) => setTitle(e.target.value)} />
        </div>

        <div className={css['create-item-input-outlined']}>
          <label>Auction URL
            <br /> <span className={css['label-notice']}>(cannot be changed after creation; recommended to include the current year)</span>
          </label>
          <input id="editAuctionCode" type='text'
            value={inputValue(auctionCode)}
            disabled={true}
          />
          <div className={css['full-auction-link']}>
            <div>
              <label>Full Auction Marketing URL:</label>
              <span>(available once the auction is public)</span>
            </div>

            <div className={css['link-check-container']}>
              <div> <input
                type="checkbox"
                checked={withEmail}
                onChange={(e) => setWithEmail(e.target.checked)}
              /> With Email
              </div>
              <div> <input
                type="checkbox"
                checked={withPublicId}
                onChange={(e) => setWithPublicId(e.target.checked)}
              /> With Public ID
              </div>
            </div>

            <div>
              {copied && <p className={css['copied-alert']}>Copied!</p>}
              <button onClick={handleCopy} >
                <img src={copyIcon} alt="Copy Auction URL" />
              </button>
              <a className={themeCss['bold-nodecoration']} href={linkToCopy} target="_blank" rel="noreferrer">
                {linkToCopy}
              </a>
            </div>

          </div>
        </div>

        <div className={css['edit-item-input']}>
          <TextEditor value={description} onChange={setDescription} isReadOnly={isReadOnly || isSrApproved} editorId="editAuctionDescription" />
        </div>
        <div className={css['edit-item-input']}>
          <label>Auction Location</label>
          <input id="editAuctionLocation" type='text'
            placeholder='e.g. Los Angeles, CA'
            value={inputValue(auctionLocation)}
            disabled={isReadOnly}
            onChange={(e) => setAuctionLocation(e.target.value)} />
        </div>

        <div className={css['edit-item-input']}>
          <label>Start Time (Pacific Time)</label>
          <input id="editAuctionStartTime" type='datetime-local'
            disabled={isReadOnly}
            value={inputValue(pacificStartTime)}
            onChange={(e) => setPacificStartTime(e.target.value)} />
          {invalidStart && <p className={themeCss['error-text']}>{invalidStart}</p>}
        </div>
        <div className={css['edit-item-input']}>
          <label>End Time (Pacific Time)</label>
          <input id="editAuctionEndTime" type='datetime-local'
            disabled={isReadOnly}
            value={inputValue(pacificEndTime)}
            onChange={(e) => setPacificEndTime(e.target.value)} />
          {invalidEnd && <p className={themeCss['error-text']}>{invalidEnd}</p>}
        </div>

        <div className={css['edit-item-input']}>
          <label>Contact Email for Auction Support</label>
          <input id="editAuctionSupportEmail" type='text'
            placeholder='e.g. ???@ucla.edu'
            value={inputValue(supportEmail)}
            disabled={isReadOnly}
            onChange={(e) => setSupportEmail(e.target.value)} />
        </div>

        {/* EventRegSiteUrl */}
        <div className={css['edit-item-input']}>
          <label>Event registration site URL (optional):</label>
          <input id="editEventRegSiteUrl" type='text'
            placeholder='e.g. https://giving.ucla.edu/short_url/'
            value={inputValue(eventRegSiteUrl)}
            disabled={isReadOnly}
            onChange={(e) => setEventRegSiteUrl(e.target.value)} />
        </div>

        {isAuctionOwner(auction) &&
          <AuctionEditPermissions permissions={permissions} setPermissions={setPermissions} isReadOnly={isReadOnly} auctionChanges={auctionChanges} />}

        {/* <div className={css['create-item-input-outlined']}>
          <label>Require in-person check in for auction participation?</label>
          <select onChange={handleNeedCheckin} value={needsCheckin === null || needsCheckin === undefined ? '' : needsCheckin ? 'true' : 'false'} disabled={isReadOnly}
            id="editAuctionRequireCheckin">
            {typeof (needsCheckin) !== 'boolean' && <option value=''>-</option>}
            <option value='true'>Yes</option>
            <option value='false'>No</option>
          </select>
          <p>"Yes" will generate a QR code for the attendee. This QR code will be required to be scanned at the event in order for the attendee to participate in the auction. Answer "Yes" for events requiring a ticket for physical in-person auction events.</p>
          <p>"No" will automatically check the user in upon registration. Answer "No" if the auction will be open online for bidding before the physical event occurs, or for free events.</p>
        </div> */}

        <div className={css['create-item-input-outlined']}>
          <label>Allow visitors to see auction items before registering?</label>
          <select onChange={handleAllowVisitorView} value={allowVisitorView === null || allowVisitorView === undefined ? '' : allowVisitorView ? 'true' : 'false'} disabled={isReadOnly}
            id="editAuctionRequireCheckin">
            {typeof (allowVisitorView) !== 'boolean' && <option value=''>-</option>}
            <option value='true'>Yes</option>
            <option value='false'>No</option>
          </select>
          <p>"Yes" will show the auction's items, without price, to everyone who visits the auction link, even before bidding starts.</p>
          <p>"No" will hide the auction items for unregistered site visitors. Once a visitor has completed the auction registration process, they will then be able to see the auction items.</p>
        </div>

        
        
        <div className={css['edit-item-input']}>
          <InputFundSearch auctionChanges={auctionChanges} auction={auction}
            fundNumber={fundNumber} setFundNumber={setFundNumber}
            fundName={fundName} setFundName={setFundName}
            fundNumberInput={fundNumberInput} setFundNumberInput={setFundNumberInput}
            isReadOnly={isReadOnly || isSrApproved} 
          />

        </div>
        
        <div className={css['edit-item-input']}>
          <InputMarketingEffort marketingEffortName={marketingEffortName} setMarketingEffortName={setMarketingEffortName}
            marketingEffortNumber={marketingEffortNumber} setMarketingEffortNumber={setMarketingEffortNumber}
            marketingEffortInput = {marketingEffortInput} setMarketingEffortInput={setMarketingEffortInput} 
            isReadOnly={isReadOnly || isSrApproved}
            auction={auction} auctionChanges={auctionChanges} />
        </div>

        {/* <div className={css['create-item-input-outlined']}>
          <label>Allow attendees to donate directly to this fund via the auction site?</label>
           <p>
            <input
              type="radio" name="hasDonation" value="yes"
              checked={hasDonation === true}
              disabled={isReadOnly}
              onChange={() => setHasDonation(true)}
            />
            Yes, include option for donors to donate directly to this fund.
          </p>

          <p>
            <input
              type="radio" name="hasDonation" value="no"
              checked={hasDonation === false}
              disabled={isReadOnly}
              onChange={() => setHasDonation(false)}
            />
            No, do not allow donors to donate directly to this fund.
          </p>

          {hasDonation === true &&
          <div>

<p>
  Direct link to donation: {window.location.origin}/auctionhome?auctionId={auctionCode}&donation=true
</p>
            <div className={css['create-item-input-outlined']}>
              <label>Do you wish to display a fundraising goal for donating to this fund?</label>
              <p>
                <input
                  type="radio" name="includeFundraisingGoal" value="yes"
                  checked={includeFundraisingGoal === true}
                  disabled={isReadOnly}
                  onChange={() => setIncludeFundraisingGoal(true)}
                />
                Yes, displaying fundraising goal.
              </p>

              <p>
                <input
                  type="radio" name="includeFundraisingGoal" value="no"
                  checked={includeFundraisingGoal === false}
                  disabled={isReadOnly}
                  onChange={() => setIncludeFundraisingGoal(false)}
                />
                No, do not display fundraising goal.
              </p>

              {includeFundraisingGoal === true && 
              <div className={css['edit-item-input']}>
                <label>Fundraising Goal Amount</label>
                <input id="editAuctionFundraisingGoal" type='number'
                  placeholder='e.g. 10000'
                  value={inputValue(fundraisingGoal)}
                  disabled={isReadOnly}
                  onChange={(e) => setFundraisingGoal(e.target.value)} />
              </div>}
            </div>

            <div className={css['edit-item-input']}>
              <label>Low Donation Button Amount</label>
              <input id="editAuctionLowDonationAmount" type='number'
                placeholder='e.g. 50'
                value={inputValue(lowDonationAmount)}
                disabled={isReadOnly}
                onChange={(e) => setLowDonationAmount(e.target.value)} />
            </div>

            <div className={css['edit-item-input']}>
              <label>Middle Donation Button Amount</label>
              <input id="editAuctionMiddleDonationAmount" type='number'
                placeholder='e.g. 100'
                value={inputValue(midDonationAmount)}
                disabled={isReadOnly}
                onChange={(e) => setMidDonationAmount(e.target.value)} />
            </div>

            <div className={css['edit-item-input']}>
              <label>High Donation Button Amount</label>
              <input id="editAuctionHighDonationAmount" type='number'
                placeholder='e.g. 250'
                value={inputValue(highDonationAmount)}
                disabled={isReadOnly}
                onChange={(e) => setHighDonationAmount(e.target.value)} />
            </div>

          </div>}
          
        </div> */}

        <div className={css['edit-item-input']}>
          <label>Banner Image Upload <span className={css['acceptable-text']}>(Accepted file types: .jpg, .jpeg, .png, .avif) </span></label>
          <div>
            <p>Current filename: {bannerImage}</p>
            <div className={`${css['thumbnail-container']} `}>
              <img className={themeCss['contain-image']} src={bannerUrl} alt={`${auction.Title} banner thumbnail`} />
            </div>
            <UploadFiles imageType={'auction'} auction={auction} isReadOnly={isReadOnly}
              refreshQuery={refreshAdminAuctionQuery} pageType={'editAuction'} />
          </div>
        </div>
      </div>

      {(needsRequired || invalidEnd || invalidStart) &&
        <div className={css['required-messages']}>
          {title === '' && <p>Auction Title is missing.</p>}
          {/* {auctionCode === '' ? <p>Auction Code is missing.</p> : invalidCode ? <p>Auction Code is already in use.</p> : ''} */}
          {startTime === '' ? <p>Auction Start Time is missing.</p> : invalidStart ? <p>Auction Start Time is invalid.</p> : ''}
          {endTime === '' ? <p>Auction End Time is missing.</p> : invalidEnd ? <p>Auction End Time is invalid.</p> : ''}
          {/* {supportEmail === '' && <p>Auction Support Email is missing.</p>} */}
        </div>}

      {invalidPermissions && <p className={`${css['edit-permissions-center']} ${themeCss['error-text']}`}>Permissions has an invalid selection, please fix before saving the auction.</p>}
      <button id="editAuctionSubmit"
        disabled={isReadOnly || disableEdit || needsRequired || invalidEnd || invalidStart || invalidPermissions}
        className={`${themeCss['FilledBlue']} ${themeCss['button']} ${themeCss['centered-button']}`}
        onClick={() => onSubmit()}
        type='submit'>Save Auction</button>
    </div>
  )
}
